/**
 * @fileoverview Local-first service for legacy migration manifest overrides and live schema lookups.
 * @module system-web/legacy-manifest-service
 *
 * Responsibilities:
 * - Read manifest overrides from Dexie (liveQuery-safe).
 * - POST/DELETE override mutations through system-api with optimistic-concurrency version.
 * - Fetch live SQL catalog data (online-exception) for the Manifest Editor dropdowns.
 *
 * Dependencies:
 * - `@firesquirrel/local-db` for system Dexie access.
 * - `../apiClient` `apiFetch` for API calls.
 *
 * Architecture:
 * - Manifest overrides are local-first: Dexie mirrors system Mongo via system sync.
 * - Live schema data (tables, columns, SQL-declared FKs) is an online exception:
 *   never written to Dexie, held in component state for the editor session only.
 * - saveOverride and resetOverride POST/DELETE to system-api (server validates); on success,
 *   the updated doc arrives via the next system sync pull (no local write-through needed for
 *   overrides since the server validates and writes authoritatively).
 *
 * Notes:
 * - `getOverride` is liveQuery-safe (pure read).
 * - `fetchLegacyTables` / `fetchLegacyColumns` / `fetchDeclaredFks` are online-exception reads.
 *   Callers must NOT call these inside `liveQuery`.
 */

import { getSystemDb } from '@firesquirrel/local-db';
import { apiFetch } from '../apiClient';

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * @contract ManifestOverrideRow
 * Purpose:  Local-first read shape for a manifest override document from Dexie.
 * Fields:   Mirrors the system Mongo `legacyMigrationManifestOverrides` shape.
 * Rules:    Server-authoritative; Dexie mirrors via sync.
 * Sync:     Source of truth: system Mongo; Dexie mirrors via system sync pull.
 */
export interface ManifestOverrideRow {
  _id: string;
  datasetKey: string;
  excludedTables: string[];
  addedRelationships: unknown[];
  modifiedRelationships: unknown[];
  version: number;
  updatedBy: string;
  updatedAt: string;
  history: unknown[];
  _deleted?: boolean;
}

/**
 * @contract SchemaTable
 * Purpose:  A single table row from live INFORMATION_SCHEMA.TABLES query.
 * Fields:   schema, table.
 * Rules:    Not persisted to Dexie — held in component state only.
 * Sync:     Online exception; NOT synced.
 */
export interface SchemaTable {
  schema: string;
  table: string;
}

/**
 * @contract SchemaColumn
 * Purpose:  A single column row from live INFORMATION_SCHEMA.COLUMNS query.
 * Fields:   column, dataType, isNullable, ordinalPosition, isPrimaryKey.
 * Rules:    Not persisted to Dexie — held in component state only.
 * Sync:     Online exception; NOT synced.
 */
export interface SchemaColumn {
  column: string;
  dataType: string;
  isNullable: string;
  ordinalPosition: number;
  isPrimaryKey: boolean;
}

/**
 * @contract DeclaredFk
 * Purpose:  A single actual FK from the live SQL Server catalog.
 * Fields:   parentTable, parentColumn, childTable, childColumn.
 * Rules:    Not persisted directly to Dexie. Streamline has partial FK coverage; admins add missing relationships.
 * Sync:     Online exception; NOT synced.
 */
export interface DeclaredFk {
  parentTable: string;
  parentColumn: string;
  childTable: string;
  childColumn: string;
}

/**
 * @contract SaveOverrideInput
 * Purpose:  Body for saveOverride() — mirrors the Zod schema on the server.
 * Fields:   excludedTables, addedRelationships, modifiedRelationships, version.
 * Rules:    version must match the current persisted version (optimistic concurrency).
 * Sync:     POST validates + persists server-side; Dexie pulls via system sync.
 */
export interface SaveOverrideInput {
  excludedTables: string[];
  addedRelationships: unknown[];
  modifiedRelationships: unknown[];
  version: number;
}

// ---------------------------------------------------------------------------
// Dexie reads (liveQuery-safe — pure reads, no writes)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: system Dexie `legacy_migration_manifest_overrides` (mirrored from Mongo).
 * Write Flow:      saveOverride() posts to server; pull brings the new doc into Dexie.
 * Sync Strategy:   System sync pull mirrors legacyMigrationManifestOverrides.
 * Conflict Resolution: Server wins.
 *
 * Return value is suitable for use inside `liveQuery(() => getOverride(...))`.
 * NEVER write to Dexie from inside liveQuery.
 */
export async function getOverride(datasetKey: string): Promise<ManifestOverrideRow | null> {
  const db = await getSystemDb();
  const all = await db.legacy_migration_manifest_overrides.toArray();
  const row = (all as ManifestOverrideRow[]).find(
    (r) => r.datasetKey === datasetKey && !r._deleted,
  );
  return row ?? null;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: system Mongo (server-authoritative).
 * Write Flow:
 *   1. POST to /api/system/legacy-migration/manifest-overrides/:datasetKey.
 *   2. Server validates against INFORMATION_SCHEMA (Phase 1) and business rules.
 *   3. Returns the saved override doc.
 *   4. Dexie will receive the authoritative doc via the next system sync pull.
 * Sync Strategy:   Pull-based after server write.
 * Conflict Resolution: Server wins; 409 returned on version mismatch.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses. 422 includes per-field issues. 409 = version conflict.
 * User Impact: Editor surfaces the error inline per-field (422) or as a toast (409/500).
 * Logging:    REQUIRED — log failures with datasetKey.
 */
export async function saveOverride(
  datasetKey: string,
  input: SaveOverrideInput,
): Promise<ManifestOverrideRow> {
  const result = await apiFetch(
    `/api/system/legacy-migration/manifest-overrides/${encodeURIComponent(datasetKey)}`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  return result.override as ManifestOverrideRow;
}

/**
 * @localFirst
 * Source of Truth: system Mongo (server-authoritative).
 * Write Flow:
 *   1. DELETE to /api/system/legacy-migration/manifest-overrides/:datasetKey.
 *   2. Server soft-deletes the override doc (`_deleted: true`).
 *   3. Dexie receives the tombstone via the next system sync pull.
 * Sync Strategy:   Pull-based after server soft-delete.
 * Conflict Resolution: Server wins.
 *
 * @errorHandling
 * Strategy:   Throws on non-200 responses.
 * User Impact: Reset button shows error toast; override is not deleted.
 * Logging:    REQUIRED — log failures with datasetKey.
 */
export async function resetOverride(datasetKey: string): Promise<void> {
  await apiFetch(
    `/api/system/legacy-migration/manifest-overrides/${encodeURIComponent(datasetKey)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Online-exception: live schema reads (INFORMATION_SCHEMA)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: Streamline SQL INFORMATION_SCHEMA (online exception — not in our system).
 * Write Flow:      NOT written to Dexie — held in component state for the editor session.
 * Sync Strategy:   Not applicable.
 * Conflict Resolution: Not applicable.
 *
 * MUST be called imperatively (button click, mount effect), never inside `liveQuery`.
 *
 * @errorHandling
 * Strategy:   Throws on non-OK responses. Caller surfaces an error message in the editor.
 * User Impact: Editor shows "Could not load schema — check legacy DB connection" inline.
 * Logging:    REQUIRED — log failures with legacyDb.
 */
export async function fetchLegacyTables(legacyDb: string): Promise<SchemaTable[]> {
  const result = await apiFetch(
    `/api/system/legacy-migration/schema/${encodeURIComponent(legacyDb)}/tables`,
  );
  return (result.tables ?? []) as SchemaTable[];
}

/**
 * @localFirst
 * Source of Truth: Streamline SQL INFORMATION_SCHEMA.COLUMNS (online exception).
 * Write Flow:      NOT written to Dexie — component state only.
 * Sync Strategy:   Not applicable.
 * Conflict Resolution: Not applicable.
 *
 * MUST be called imperatively, never inside `liveQuery`.
 *
 * @errorHandling
 * Strategy:   Throws on non-OK responses.
 * User Impact: Column dropdown shows error; admin can retry via [Refresh schema].
 * Logging:    REQUIRED — log failures with legacyDb + table.
 */
export async function fetchLegacyColumns(legacyDb: string, table: string): Promise<SchemaColumn[]> {
  const result = await apiFetch(
    `/api/system/legacy-migration/schema/${encodeURIComponent(legacyDb)}/tables/${encodeURIComponent(table)}/columns`,
  );
  return (result.columns ?? []) as SchemaColumn[];
}

/**
 * @localFirst
 * Source of Truth: Streamline SQL INFORMATION_SCHEMA.REFERENTIAL_CONSTRAINTS (online exception).
 * Write Flow:      NOT written to Dexie — component state only.
 * Sync Strategy:   Not applicable.
 * Conflict Resolution: Not applicable.
 *
 * MUST be called imperatively, never inside `liveQuery`.
 *
 * @errorHandling
 * Strategy:   Throws on non-OK responses.
 * User Impact: Existing database FKs seed the schema manifest; admins fill gaps manually.
 * Logging:    REQUIRED — log failures with legacyDb.
 */
export async function fetchDeclaredFks(legacyDb: string, childTable?: string): Promise<DeclaredFk[]> {
  const qs = childTable ? `?childTable=${encodeURIComponent(childTable)}` : '';
  const result = await apiFetch(
    `/api/system/legacy-migration/schema/${encodeURIComponent(legacyDb)}/declared-fks${qs}`,
  );
  return (result.fks ?? []) as DeclaredFk[];
}
